/**
 * Maps a keydown event to a schedule-editor command. Pure and DOM-light so the
 * mapping (including the "don't hijack typing in an input" rule) is unit-tested
 * without a real DOM. The component owns what each command actually does.
 */

export type ScheduleShortcut = "undo" | "redo" | "copy" | "cut" | "paste";

export interface ShortcutEventLike {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  target?: EventTarget | null;
}

/** True when the event originates from a field where the user is typing. */
export function isFromTextField(target: EventTarget | null | undefined): boolean {
  const el = target as (HTMLElement & { isContentEditable?: boolean }) | null;
  if (!el || !el.tagName) return false;
  return el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable === true;
}

/**
 * Resolve the command for an event, or null when nothing should happen.
 * Requires the platform modifier (Cmd on macOS, Ctrl elsewhere) and ignores
 * events raised while typing in a text field.
 */
export function matchScheduleShortcut(e: ShortcutEventLike): ScheduleShortcut | null {
  if (!(e.metaKey || e.ctrlKey)) return null;
  if (isFromTextField(e.target)) return null;

  switch (e.key.toLowerCase()) {
    case "z":
      // Cmd/Ctrl+Shift+Z redoes; plain Cmd/Ctrl+Z undoes.
      return e.shiftKey ? "redo" : "undo";
    case "y":
      // Windows-style redo.
      return "redo";
    case "c":
      return "copy";
    case "x":
      return "cut";
    case "v":
      return "paste";
    default:
      return null;
  }
}
